import { useState, useEffect } from 'react';
import axios from 'axios';
import {
  Target, Trophy, UserPlus, ShoppingCart, RefreshCw,
  CheckCircle2, FileText, UserCheck, CreditCard,
  ChevronLeft, Users, TrendingUp, Award,
} from 'lucide-react';
import { Card } from './ui/card';
import { Button } from './ui/button';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './ui/select';
import { toast } from 'sonner';

const API = process.env.REACT_APP_BACKEND_URL;

const OBJ_ICONS = {
  'user-plus':    UserPlus,
  'shopping-cart': ShoppingCart,
  'refresh-cw':   RefreshCw,
};
const STEP_ICONS = {
  'file-text':   FileText,
  'user-check':  UserCheck,
  'credit-card': CreditCard,
};
const COLOR_CFG = {
  emerald: { bg: 'bg-emerald-100', iconColor: 'text-emerald-600', ring: 'ring-emerald-200', accent: 'bg-emerald-500', textAccent: 'text-emerald-700', badgeBg: 'bg-emerald-50' },
  blue:    { bg: 'bg-blue-100',    iconColor: 'text-blue-600',    ring: 'ring-blue-200',    accent: 'bg-blue-500',    textAccent: 'text-blue-700',    badgeBg: 'bg-blue-50' },
  violet:  { bg: 'bg-violet-100',  iconColor: 'text-violet-600',  ring: 'ring-violet-200',  accent: 'bg-violet-500',  textAccent: 'text-violet-700',  badgeBg: 'bg-violet-50' },
};

function monthOptions() {
  const opts = [];
  const now = new Date();
  for (let i = 0; i < 12; i++) {
    const d = new Date(now.getFullYear(), now.getMonth() - i, 1);
    const value = `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}`;
    const label = d.toLocaleDateString('fr-FR', { month: 'long', year: 'numeric' });
    opts.push({ value, label: label.charAt(0).toUpperCase() + label.slice(1) });
  }
  return opts;
}

function formatDateFR(iso) {
  if (!iso) return '';
  try {
    const d = new Date(iso);
    return `${String(d.getDate()).padStart(2, '0')}/${String(d.getMonth() + 1).padStart(2, '0')}/${d.getFullYear()} à ${String(d.getHours()).padStart(2, '0')}:${String(d.getMinutes()).padStart(2, '0')}`;
  } catch {
    return iso.slice(0, 16);
  }
}

export default function ObjectifsTab({ currentUser }) {
  const isAdmin = (currentUser?.role || '').toLowerCase() === 'admin';
  const [tab, setTab] = useState('me'); // 'me' | 'equipe' (admin only)
  const [mois, setMois] = useState(monthOptions()[0].value);
  const [data, setData] = useState(null);
  const [equipeData, setEquipeData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [selectedObj, setSelectedObj] = useState(null); // détail d'un objectif

  const loadMe = async () => {
    setLoading(true);
    try {
      const { data } = await axios.get(`${API}/api/objectifs/me`, { params: { mois } });
      setData(data);
    } catch { toast.error('Erreur de chargement des objectifs'); }
    finally { setLoading(false); }
  };
  const loadEquipe = async () => {
    setLoading(true);
    try {
      const { data } = await axios.get(`${API}/api/objectifs/equipe`, { params: { mois } });
      setEquipeData(data);
    } catch { toast.error('Erreur de chargement équipe'); }
    finally { setLoading(false); }
  };

  useEffect(() => {
    if (tab === 'me') loadMe();
    else loadEquipe();
    setSelectedObj(null);
    /* eslint-disable-next-line */
  }, [tab, mois]);

  if (loading && !data && !equipeData) {
    return (
      <div className="flex items-center justify-center py-20">
        <div className="animate-spin rounded-full h-12 w-12 border-b-4 border-blue-600"></div>
      </div>
    );
  }

  // Vue détail d'un objectif
  if (selectedObj && data) {
    return (
      <ObjectifDetailView
        objectif={data.objectifs.find(o => o.id === selectedObj)}
        global={data}
        mois={mois}
        moisOptions={monthOptions()}
        onChangeMois={setMois}
        onBack={() => setSelectedObj(null)}
      />
    );
  }

  return (
    <div className="space-y-5">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-end sm:justify-between gap-3">
        <div>
          <h2 className="text-2xl font-bold text-gray-900">Mes objectifs</h2>
          <p className="text-sm text-gray-500 mt-1">
            Atteignez chaque étape du processus et gagnez des points !
          </p>
        </div>
        <div className="flex items-center gap-2 flex-wrap">
          {isAdmin && (
            <div className="inline-flex bg-gray-100 rounded-lg p-1">
              <button
                onClick={() => setTab('me')}
                className={`px-3 py-1.5 text-sm font-semibold rounded-md transition-all ${
                  tab === 'me' ? 'bg-white text-gray-900 shadow' : 'text-gray-600 hover:text-gray-900'
                }`}
                data-testid="tab-mes-objectifs"
              >
                Mes objectifs
              </button>
              <button
                onClick={() => setTab('equipe')}
                className={`px-3 py-1.5 text-sm font-semibold rounded-md transition-all ${
                  tab === 'equipe' ? 'bg-white text-gray-900 shadow' : 'text-gray-600 hover:text-gray-900'
                }`}
                data-testid="tab-equipe"
              >
                <Users className="h-3.5 w-3.5 inline mr-1" /> Équipe
              </button>
            </div>
          )}
          <Select value={mois} onValueChange={setMois}>
            <SelectTrigger className="w-44 bg-white" data-testid="mois-select">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {monthOptions().map(o => (
                <SelectItem key={o.value} value={o.value}>{o.label}</SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
      </div>

      {tab === 'me' && data && (
        <>
          {/* KPIs en haut à droite */}
          <div className="grid grid-cols-2 gap-3 sm:flex sm:justify-end">
            <KpiCard
              icon={<Target className="h-5 w-5 text-violet-600" />}
              iconBg="bg-violet-100"
              value={data.total_points}
              label="Points"
            />
            <KpiCard
              icon={<Trophy className="h-5 w-5 text-amber-500" />}
              iconBg="bg-amber-100"
              value={`${data.completed_objectifs} / ${data.total_objectifs}`}
              label="Objectifs complétés"
            />
          </div>

          {/* 3 cartes objectifs */}
          <div className="space-y-4">
            {data.objectifs.map(obj => (
              <ObjectifCard
                key={obj.id}
                objectif={obj}
                onClick={() => setSelectedObj(obj.id)}
              />
            ))}
          </div>

          {/* Footer info */}
          <div className="bg-blue-50 border border-blue-100 rounded-lg px-4 py-3 text-sm text-blue-900 flex items-start gap-2">
            <CheckCircle2 className="h-4 w-4 mt-0.5 text-blue-600 flex-shrink-0" />
            <span>Chaque étape complétée vous rapproche de votre objectif et vous fait gagner des points !</span>
          </div>
        </>
      )}

      {tab === 'equipe' && equipeData && (
        <EquipeView data={equipeData} />
      )}
    </div>
  );
}

function KpiCard({ icon, iconBg, value, label }) {
  return (
    <Card className="p-3 sm:p-4 flex items-center gap-3 min-w-[140px]">
      <div className={`w-10 h-10 rounded-xl ${iconBg} flex items-center justify-center flex-shrink-0`}>
        {icon}
      </div>
      <div>
        <div className="text-xl font-bold text-gray-900 leading-none">{value}</div>
        <div className="text-xs text-gray-500 mt-1">{label}</div>
      </div>
    </Card>
  );
}

function ObjectifCard({ objectif, onClick }) {
  const Icon = OBJ_ICONS[objectif.icon] || Target;
  const c = COLOR_CFG[objectif.color] || COLOR_CFG.emerald;
  const firstWaitingIdx = objectif.steps.findIndex(s => !s.done);
  const lastDoneIdx = objectif.steps.map(s => s.done).lastIndexOf(true);

  return (
    <Card
      onClick={onClick}
      className="p-5 cursor-pointer hover:shadow-lg transition-all"
      data-testid={`objectif-card-${objectif.id}`}
    >
      <div className="flex items-start justify-between gap-4 mb-5">
        <div className="flex items-start gap-3">
          <div className={`w-12 h-12 rounded-full ${c.bg} flex items-center justify-center flex-shrink-0`}>
            <Icon className={`h-6 w-6 ${c.iconColor}`} />
          </div>
          <div>
            <h3 className="text-lg font-bold text-gray-900">Objectif {objectif.id === 'transformation' ? '1' : objectif.id === 'upsell' ? '2' : '3'} : {objectif.titre}</h3>
            <p className="text-sm text-gray-500 mt-0.5">{objectif.description}</p>
          </div>
        </div>
        <div className="inline-flex items-center gap-1 bg-emerald-50 text-emerald-700 px-3 py-1.5 rounded-lg font-semibold text-sm flex-shrink-0">
          <Award className="h-4 w-4" /> {objectif.points} / {objectif.points_max} pts
        </div>
      </div>

      {/* Timeline horizontale */}
      <StepsTimeline steps={objectif.steps} color={objectif.color} compact firstWaitingIdx={firstWaitingIdx} lastDoneIdx={lastDoneIdx} />

      {/* Progression bar */}
      <div className="mt-4 flex items-center gap-3">
        <div className="flex-1 h-2 bg-gray-100 rounded-full overflow-hidden">
          <div
            className={`h-full ${c.accent} transition-all`}
            style={{ width: `${objectif.progression_pct}%` }}
          ></div>
        </div>
        <span className="text-xs font-semibold text-gray-600 tabular-nums">{objectif.progression_pct}%</span>
      </div>
    </Card>
  );
}

function StepsTimeline({ steps, color, compact = false, firstWaitingIdx = -1, lastDoneIdx = -1 }) {
  const c = COLOR_CFG[color] || COLOR_CFG.emerald;
  return (
    <div className="relative">
      <div className="flex items-start justify-between gap-1">
        {steps.map((step, idx) => {
          const Icon = STEP_ICONS[step.icon] || FileText;
          const isCurrent = !step.done && idx === firstWaitingIdx;
          const isConnectorDone = idx > 0 && (steps[idx - 1].done && (step.done || idx <= lastDoneIdx + 1));

          return (
            <div key={step.key} className="flex-1 flex flex-col items-center relative min-w-0">
              {/* Connecteur vers étape précédente */}
              {idx > 0 && (
                <div
                  className={`absolute top-5 sm:top-6 right-1/2 left-[-50%] h-0.5 ${
                    isConnectorDone ? c.accent : 'bg-gray-200'
                  }`}
                ></div>
              )}
              {/* Cercle étape */}
              <div
                className={`relative w-10 h-10 sm:w-12 sm:h-12 rounded-full flex items-center justify-center flex-shrink-0 ${
                  step.done
                    ? `${c.accent} text-white`
                    : isCurrent
                      ? `bg-white ring-4 ${c.ring} ${c.bg}`
                      : 'bg-gray-100'
                }`}
              >
                {step.done ? (
                  <CheckCircle2 className="h-5 w-5 sm:h-6 sm:w-6" />
                ) : (
                  <Icon className={`h-4 w-4 sm:h-5 sm:w-5 ${isCurrent ? c.iconColor : 'text-gray-400'}`} />
                )}
              </div>

              <div className="text-center mt-2">
                <div className={`text-[11px] sm:text-xs font-semibold ${step.done ? 'text-gray-900' : 'text-gray-700'} line-clamp-2 px-1`}>
                  {idx + 1}. {step.label}
                </div>
                <div className={`text-[10px] sm:text-xs mt-0.5 ${
                  step.done ? c.textAccent : isCurrent ? c.textAccent : 'text-gray-400'
                }`}>
                  {step.done ? 'Terminé' : isCurrent ? 'En attente' : 'En attente'}
                </div>
                {!compact && step.done && step.completed_at && (
                  <div className="text-[10px] text-gray-400 mt-0.5">
                    Le {formatDateFR(step.completed_at)}
                  </div>
                )}
                {!compact && step.done && step.nom && (
                  <div className={`text-[10px] mt-1 font-medium ${c.textAccent} truncate`}>
                    {step.nom}
                  </div>
                )}
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}

function ObjectifDetailView({ objectif, global, mois, moisOptions, onChangeMois, onBack }) {
  if (!objectif) {
    return <div className="text-center py-10 text-gray-500">Objectif introuvable</div>;
  }
  const c = COLOR_CFG[objectif.color] || COLOR_CFG.emerald;
  const Icon = OBJ_ICONS[objectif.icon] || Target;
  const firstWaiting = objectif.steps.find(s => !s.done);
  const firstWaitingIdx = objectif.steps.findIndex(s => !s.done);
  const lastDoneIdx = objectif.steps.map(s => s.done).lastIndexOf(true);
  const stepsDone = objectif.steps.filter(s => s.done);

  return (
    <div className="space-y-5">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-end sm:justify-between gap-3">
        <div className="flex items-center gap-3">
          <Button variant="outline" onClick={onBack} size="sm" data-testid="back-objectifs">
            <ChevronLeft className="h-4 w-4 mr-1" /> Retour
          </Button>
          <div>
            <h2 className="text-2xl font-bold text-gray-900">Mes objectifs</h2>
            <p className="text-sm text-gray-500 mt-0.5">
              Atteignez chaque étape du processus et gagnez des points !
            </p>
          </div>
        </div>
        <Select value={mois} onValueChange={onChangeMois}>
          <SelectTrigger className="w-44 bg-white">
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            {moisOptions.map(o => (
              <SelectItem key={o.value} value={o.value}>{o.label}</SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>

      {/* Carte principale */}
      <Card className="p-5 sm:p-6">
        <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-4 mb-6">
          <div className="flex items-start gap-3">
            <div className={`w-12 h-12 rounded-full ${c.bg} flex items-center justify-center flex-shrink-0`}>
              <Icon className={`h-6 w-6 ${c.iconColor}`} />
            </div>
            <div>
              <h3 className="text-xl font-bold text-gray-900">Objectif : {objectif.titre}</h3>
              <p className="text-sm text-gray-500 mt-0.5">{objectif.description}</p>
            </div>
          </div>
          <div className="flex gap-3">
            <KpiCard icon={<Target className="h-5 w-5 text-violet-600" />} iconBg="bg-violet-100" value={objectif.points} label="Points" />
            <KpiCard icon={<Trophy className="h-5 w-5 text-amber-500" />} iconBg="bg-amber-100" value={`${objectif.completed_count} / ${objectif.total_steps}`} label="Étapes complétées" />
          </div>
        </div>

        {/* Timeline détaillée */}
        <StepsTimeline steps={objectif.steps} color={objectif.color} firstWaitingIdx={firstWaitingIdx} lastDoneIdx={lastDoneIdx} />

        {/* Prochaine étape */}
        {firstWaiting && (
          <div className={`mt-6 ${c.badgeBg} border ${c.ring.replace('ring-', 'border-')} rounded-xl p-4 flex flex-col sm:flex-row items-start sm:items-center justify-between gap-3`}>
            <div className="flex items-start gap-3 min-w-0">
              <TrendingUp className={`h-5 w-5 ${c.iconColor} flex-shrink-0 mt-0.5`} />
              <div className="min-w-0">
                <div className={`font-bold ${c.textAccent}`}>Prochaine étape : {firstWaiting.label}</div>
                <div className="text-sm text-gray-700 mt-0.5">
                  Complétez cette étape pour avancer vers votre objectif.
                </div>
              </div>
            </div>
            {/* Bouton contextuel */}
            <NextStepCTA stepKey={firstWaiting.key} color={c} />
          </div>
        )}
      </Card>

      {/* Bas : Progression + Historique */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        <Card className="p-5">
          <h3 className="font-bold text-gray-900 mb-4">Mes objectifs du mois</h3>
          <div className="flex items-center gap-5">
            <CircularProgress percent={global.progression_pct} color={c.accent} />
            <div className="space-y-3 flex-1">
              <Stat label="Objectifs complétés" value={`${global.completed_objectifs} / ${global.total_objectifs}`} />
              <Stat label="Points gagnés" value={global.total_points} />
              <Stat label="Points disponibles" value={global.total_points_max} />
            </div>
          </div>
        </Card>

        <Card className="p-5">
          <h3 className="font-bold text-gray-900 mb-4">Historique des étapes</h3>
          {stepsDone.length === 0 ? (
            <p className="text-sm text-gray-500">Aucune étape complétée pour le moment.</p>
          ) : (
            <div className="space-y-3">
              {stepsDone.map(s => (
                <div key={s.key} className="flex items-center justify-between gap-3 text-sm">
                  <div className="flex items-center gap-2 min-w-0">
                    <CheckCircle2 className="h-4 w-4 text-emerald-600 flex-shrink-0" />
                    <span className="font-medium text-gray-800 truncate">{s.label}</span>
                  </div>
                  <span className="text-xs text-gray-500 whitespace-nowrap">
                    {s.completed_at && `Le ${formatDateFR(s.completed_at)}`}
                  </span>
                  <span className="text-xs font-bold text-emerald-700 whitespace-nowrap">+10 points</span>
                </div>
              ))}
            </div>
          )}
        </Card>
      </div>

      <div className="bg-blue-50 border border-blue-100 rounded-lg px-4 py-3 text-sm text-blue-900 flex items-start gap-2">
        <CheckCircle2 className="h-4 w-4 mt-0.5 text-blue-600 flex-shrink-0" />
        <span>Chaque étape complétée vous rapproche de votre objectif et vous fait gagner des points !</span>
      </div>
    </div>
  );
}

function NextStepCTA({ stepKey, color }) {
  const map = {
    devis_cree:          { label: 'Créer un devis',         link: '/contrats' },
    devis_accepte:       { label: 'Voir mes devis',          link: '/contrats' },
    contrat_cree:        { label: 'Créer un contrat',        link: '/contrats' },
    prospect_to_client:  { label: 'Voir mes prospects',      link: '/prospects' },
    acceptation_client:  { label: 'Voir mes contrats',       link: '/contrats' },
    client_renouvele:    { label: 'Voir mes clients',        link: '/clients' },
    paiement:            { label: 'Voir les factures',       link: '/factures' },
    facture_creee:       { label: 'Créer une facture',       link: '/factures' },
  };
  const cta = map[stepKey];
  if (!cta) return null;
  return (
    <Button
      onClick={() => { window.location.href = cta.link; }}
      className={`${color.accent} hover:opacity-90 text-white whitespace-nowrap flex-shrink-0`}
      data-testid={`next-step-cta-${stepKey}`}
    >
      {cta.label}
    </Button>
  );
}

function CircularProgress({ percent, color }) {
  const radius = 42;
  const circ = 2 * Math.PI * radius;
  const offset = circ - (percent / 100) * circ;
  return (
    <div className="relative w-28 h-28 flex-shrink-0">
      <svg viewBox="0 0 100 100" className="w-full h-full -rotate-90">
        <circle cx="50" cy="50" r={radius} stroke="#e5e7eb" strokeWidth="8" fill="none" />
        <circle
          cx="50" cy="50" r={radius}
          className={color.replace('bg-', 'stroke-')}
          strokeWidth="8" fill="none" strokeLinecap="round"
          strokeDasharray={circ} strokeDashoffset={offset}
          style={{ transition: 'stroke-dashoffset 0.5s' }}
        />
      </svg>
      <div className="absolute inset-0 flex flex-col items-center justify-center">
        <span className="text-2xl font-bold text-gray-900">{percent}%</span>
        <span className="text-xs text-gray-500">Progression</span>
      </div>
    </div>
  );
}

function Stat({ label, value }) {
  return (
    <div className="flex items-center justify-between">
      <span className="text-sm text-gray-600">{label}</span>
      <span className="text-base font-bold text-gray-900">{value}</span>
    </div>
  );
}

function EquipeView({ data }) {
  return (
    <div className="space-y-4">
      <Card className="p-5">
        <div className="flex items-center justify-between mb-4">
          <h3 className="font-bold text-gray-900">Classement de l'équipe — {data.mois}</h3>
          <span className="text-xs text-gray-500">{data.equipe.length} commerciaux</span>
        </div>
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead className="text-xs uppercase tracking-wider text-gray-500 border-b">
              <tr>
                <th className="text-left py-2 pl-2">#</th>
                <th className="text-left py-2">Commercial</th>
                <th className="text-center py-2">Obj. 1 (Transf.)</th>
                <th className="text-center py-2">Obj. 2 (Upsell)</th>
                <th className="text-center py-2">Obj. 3 (Renouv.)</th>
                <th className="text-center py-2">Progression</th>
                <th className="text-right py-2 pr-2">Points</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100">
              {data.equipe.length === 0 && (
                <tr><td colSpan={7} className="text-center py-6 text-gray-500">Aucun commercial trouvé</td></tr>
              )}
              {data.equipe.map((emp, idx) => (
                <tr key={emp.email} className="hover:bg-gray-50">
                  <td className="py-3 pl-2">
                    <RankBadge rank={idx + 1} />
                  </td>
                  <td className="py-3">
                    <div className="font-medium text-gray-900">{emp.name}</div>
                    <div className="text-xs text-gray-500">{emp.email}</div>
                  </td>
                  {emp.objectifs.map(o => (
                    <td key={o.id} className="py-3 text-center">
                      <ObjMiniProgress completed={o.completed_count} total={o.total_steps} color={o.color} />
                    </td>
                  ))}
                  <td className="py-3 text-center">
                    <div className="inline-flex items-center gap-2">
                      <div className="w-20 h-2 bg-gray-100 rounded-full overflow-hidden">
                        <div className="h-full bg-emerald-500" style={{ width: `${emp.progression_pct}%` }}></div>
                      </div>
                      <span className="text-xs font-semibold text-gray-700">{emp.progression_pct}%</span>
                    </div>
                  </td>
                  <td className="py-3 pr-2 text-right">
                    <span className="font-bold text-violet-700">{emp.total_points}</span>
                    <span className="text-xs text-gray-400"> / {emp.total_points_max}</span>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </Card>
    </div>
  );
}

function RankBadge({ rank }) {
  const styles = {
    1: 'bg-amber-100 text-amber-700',
    2: 'bg-gray-200 text-gray-700',
    3: 'bg-orange-100 text-orange-700',
  };
  return (
    <span className={`inline-flex items-center justify-center w-7 h-7 rounded-full text-xs font-bold ${styles[rank] || 'bg-gray-100 text-gray-600'}`}>
      {rank}
    </span>
  );
}

function ObjMiniProgress({ completed, total, color }) {
  const c = COLOR_CFG[color] || COLOR_CFG.emerald;
  return (
    <span className={`inline-flex items-center gap-1.5 px-2 py-0.5 rounded-full text-xs font-semibold ${c.badgeBg} ${c.textAccent}`}>
      {completed} / {total}
    </span>
  );
}
